import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, CreditCard, ArrowLeft } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { trackPageview, trackEvent } from "@/lib/analytics";

const fmtEur = (cents) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

// Konfetti-Feuerwerk in Markenfarben (Gold/Schwarz/Weiß)
const fireCelebration = () => {
  const colors = ["#d4af37", "#0a0a0a", "#ffffff", "#f1e2a5"];
  confetti({ particleCount: 90, spread: 75, origin: { y: 0.25 }, colors });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0, y: 0.4 }, colors }), 250);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.4 }, colors }), 450);
  setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { y: 0.3 }, colors, scalar: 0.8 }), 700);
};

export default function BuchungPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [booking, setBooking] = useState(undefined);
  const [paying, setPaying] = useState(false);

  const load = () =>
    api.get(`/bookings/${id}/public`).then(({ data }) => setBooking(data)).catch(() => setBooking(null));

  useEffect(() => {
    document.title = "Buchung | JUSTIN HILFT";
    trackPageview("/buchung");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (params.get("bezahlt") !== "1" || !booking || booking.status === "bezahlt") return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      await load();
      if (tries >= 15) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, booking?.status]);

  // Konfetti-Feier, sobald die Zahlung bestätigt ist (einmalig pro Seitenaufruf)
  const paid = booking?.status === "bezahlt";
  const justPaid = params.get("bezahlt") === "1";
  useEffect(() => {
    if (justPaid && paid) fireCelebration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justPaid, paid]);

  const pay = async () => {
    trackEvent("bezahlen_click");
    setPaying(true);
    try {
      const { data } = await api.post(`/bookings/${id}/checkout`);
      window.location.href = data.checkout_url;
    } catch (err) {      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  if (booking === undefined) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center" data-testid="buchung-loading">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </main>
    );
  }

  if (booking === null) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center px-6" data-testid="buchung-not-found">
        <div className="text-center">
          <p className="font-display uppercase tracking-tight text-2xl mb-4">Buchung nicht gefunden</p>
          <Link to="/" className="text-sm text-zinc-500 hover:text-ink">Zur Startseite</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="buchung-page" className="min-h-screen bg-paper text-ink px-6 lg:px-12 py-12">
      <div className="max-w-2xl mx-auto">
        <p className="font-display font-bold uppercase tracking-tight text-lg mb-1">
          Justin Hilft<span className="text-gold">.</span>
        </p>
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-10">Buchung</p>

        {justPaid && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="border border-gold/40 bg-gold/5 p-8 lg:p-10 mb-8 text-center"
            data-testid="buchung-danke-state"
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.2 }}
              className="mx-auto mb-6 w-16 h-16 rounded-full bg-gold flex items-center justify-center"
            >
              <Check size={30} className="text-ink" />
            </motion.span>
            {paid ? (
              <>
                <p className="font-display uppercase tracking-tight text-2xl lg:text-3xl mb-3">Danke für deine Buchung!</p>
                <p className="text-sm lg:text-base text-zinc-600 leading-relaxed max-w-md mx-auto">
                  Deine Zahlung über <strong className="text-ink">{fmtEur(booking.price_cents)}</strong> ist eingegangen.
                  Eine Buchungsbestätigung wurde an deine E-Mail-Adresse geschickt — ich melde mich zeitnah bei dir.
                </p>
              </>
            ) : (
              <>
                <p className="font-display uppercase tracking-tight text-2xl lg:text-3xl mb-3 flex items-center justify-center gap-3">
                  <Loader2 size={22} className="animate-spin text-gold" /> Zahlung wird bestätigt
                </p>
                <p className="text-sm lg:text-base text-zinc-600 leading-relaxed max-w-md mx-auto">
                  Deine Zahlung wird gerade geprüft — die Seite aktualisiert sich automatisch.
                </p>
              </>
            )}
            <Link
              to="/"
              data-testid="buchung-danke-home-button"
              className="inline-block mt-8 bg-ink text-paper px-8 py-3.5 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Zur Startseite
            </Link>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="border border-black/10 bg-white p-8 lg:p-10"
        >
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Buchungsnummer</p>
              <p className="font-display uppercase tracking-tight text-2xl" data-testid="buchung-number">{booking.number}</p>
            </div>
            <span
              className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${
                paid ? "border-emerald-600/40 text-emerald-700 bg-emerald-600/5" : "border-gold/50 text-gold bg-gold/5"
              }`}
              data-testid="buchung-status"
            >
              {paid ? "Bezahlt" : "Zahlung offen"}
            </span>
          </div>
          <dl className="space-y-5 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Leistung</dt>
              <dd className="text-base">{booking.service_label}</dd>
              <dd className="text-zinc-600">{booking.item_label}</dd>
            </div>
            {booking.date && (
              <div>
                <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Termin</dt>
                <dd className="text-base">
                  {booking.date.split("-").reverse().join(".")} · {booking.start_time} bis {booking.end_time} Uhr
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Preis</dt>
              {booking.discount_code && (
                <dd className="text-sm text-zinc-400 line-through">{fmtEur(booking.original_price_cents)}</dd>
              )}
              <dd className="font-display text-3xl" data-testid="buchung-price">{fmtEur(booking.price_cents)}</dd>
              {booking.discount_code && (
                <dd className="text-xs text-gold mt-1" data-testid="buchung-discount">
                  Rabattcode {booking.discount_code} (−{booking.discount_percent} %) angewendet
                </dd>
              )}
              <dd className="text-xs text-zinc-500 mt-1">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).</dd>
            </div>
          </dl>

          {!paid && (
            <button
              onClick={pay}
              disabled={paying}
              data-testid="buchung-pay-button"
              className="group relative overflow-hidden w-full bg-gold text-ink py-4 mt-8 text-xs uppercase tracking-[0.3em] font-medium shadow-[0_0_24px_rgba(212,175,55,0.35)] hover:shadow-[0_0_36px_rgba(212,175,55,0.55)] transition-shadow duration-500 disabled:opacity-70"
            >
              <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative flex items-center justify-center gap-3 group-hover:text-ink transition-colors duration-500">
                {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {paying ? "Weiter zur Zahlung ..." : `Jetzt online bezahlen — ${fmtEur(booking.price_cents)}`}
              </span>
            </button>
          )}
        </motion.div>

        <Link to="/" data-testid="buchung-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-ink transition-colors duration-300 mt-8">
          <ArrowLeft size={14} /> Zurück zur Startseite
        </Link>
      </div>
    </main>
  );
}
